import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router/dom';
import { Toaster } from 'sonner';
import { ApiError } from '@/api/client';
import { AuthProvider } from '@/auth/AuthProvider';
import { ChainProvider } from '@/chain/ChainProvider';
import { TooltipProvider } from '@/ui/primitives';
import { router } from './router';

/** 4xx answers are final (429 waits for Retry-After); 5xx and network errors get two more tries with backoff. */
const retry = (count: number, e: unknown) => {
  if (e instanceof ApiError && e.status >= 400 && e.status < 500 && e.status !== 429) return false;
  return count < 2;
};
const retryDelay = (count: number, e: unknown) => (e instanceof ApiError && e.retryAfterSec !== undefined ? Math.min(60, e.retryAfterSec) * 1000 : Math.min(1000 * 2 ** count, 15_000));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: false, retry, retryDelay, staleTime: 10_000 },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={250}>
        <ChainProvider>
          <AuthProvider>
            <RouterProvider router={router} />
          </AuthProvider>
        </ChainProvider>
        <Toaster
          theme="dark"
          position="bottom-right"
          toastOptions={{ classNames: { toast: '!bg-panel !border-line-2 !text-fg', description: '!text-fg-3', actionButton: '!bg-accent !text-on-accent' } }}
        />
      </TooltipProvider>
    </QueryClientProvider>
  );
}
